import { AddAlert } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import { Box, Button, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link } from "react-router-dom";
import ApiClient from '../config/ApiClient';
import { AthletePaymentAssignment, RelEntity } from '../object-actions/types/types';
import { canAccessPreAssessment } from '../subscriptions/purposeEntitlements';
import { assignmentHasCoachingSupport } from '../utils/onboarding';
import CompletedContentIcon from './CompletedContentIcon';

interface PreAssessmentCtaProps {
    assignment: AthletePaymentAssignment;
}

const PreAssessmentCta: React.FC<PreAssessmentCtaProps> = ({ assignment }) => {
    // State for reminder email sending
    const [sendingReminder, setSendingReminder] = useState(false);
    const [reminderSent, setReminderSent] = useState(false);
    const [reminderError, setReminderError] = useState<string | null>(null);

    // Handler for sending assessment reminder email
    const handleSendReminder = async () => {
        const assignmentId = assignment.paymentassignment_id;
        if (!assignmentId) {
            setReminderError('Assignment ID not found');
            return;
        }

        setSendingReminder(true);
        setReminderError(null);

        try {
            const response = await ApiClient.post(`/api/payment-assignments/${assignmentId}/send-assessment-reminder/`, {});

            if (response.success) {
                setReminderSent(true);
            } else {
                setReminderError(response.error || 'Failed to send reminder email');
            }
        } catch (error) {
            setReminderError(error instanceof Error ? error.message : 'An error occurred while sending the reminder');
        } finally {
            setSendingReminder(false);
        }
    };

    if (assignment.my_roles.includes('athlete') && !canAccessPreAssessment(assignment)) {
        return null;
    }

    if (assignment.waiver_required) {
        return (
            <Box data-testid="pre-assessment-cta--waiver-required">
                <Button
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/assignments/${assignment.paymentassignment_id}/onboarding`}
                >
                    Sign your waiver to continue
                </Button>
            </Box>
        );
    }

    if (
        assignment.my_roles.includes('athlete') &&
        !assignment.pre_assessment_submitted_at &&
        !assignmentHasCoachingSupport(assignment)
    ) {
        return (
            <Box data-testid="pre-assessment-cta--coaching-required">
                <Button
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/assignments/${assignment.paymentassignment_id}/onboarding`}
                    data-testid="pre-assessment-cta-finish-coaching"
                >
                    Finish coaching setup to continue
                </Button>
            </Box>
        );
    }

    if (assignment.pre_assessment_submitted_at) {
        return <Box display="flex" alignItems="center" gap={0.75} data-custom-id={String(assignment.paymentassignment_id ?? assignment.id)} data-testid="pre-assessment-cta--submitted">
            <CompletedContentIcon purpose="pre_assessment" />
            <Typography component="span">Pre-Assessment Submitted</Typography>
        </Box>
    }


    const firstUnsubmitted = assignment.submissions.find(sub => sub.entity && !sub.entity.submitted_at) as RelEntity<'AssessmentSubmission'>;

    const questionCount = firstUnsubmitted?.entity?.response_count && firstUnsubmitted.entity.response_count > 0 ? (
        <Typography variant="body2" color="text.secondary" data-custom-id={String(assignment.paymentassignment_id ?? assignment.id)} data-testid="pre-assessment-cta--in-progress">
            {firstUnsubmitted.entity.response_count} questions answered
        </Typography>
    ) : (
        null
    );

    if (firstUnsubmitted && assignment.my_roles.includes('athlete')) {
        return <Box data-custom-id={String(assignment.paymentassignment_id ?? assignment.id)} data-testid="pre-assessment-cta--in-progress">
            <Button
                sx={{ my: 1 }}
                variant="contained"
                color="secondary"
                size="large"
                component={Link}
                to={`/assessments/${assignment.assessment_id}`}
                data-testid="pre-assessment-cta-resume"
            >
                Resume Assessment
            </Button>
            {questionCount}
        </Box>
    } else if (firstUnsubmitted && (assignment.my_roles.includes('parent') || assignment.my_roles.includes('coach'))) {
        return <Box display="flex" flexDirection="column" gap={1} data-custom-id={String(assignment.paymentassignment_id ?? assignment.id)} data-testid="pre-assessment-cta--in-progress">
            <Typography variant="body2" color="text.secondary">
                {firstUnsubmitted.entity?.response_count} responses submitted
            </Typography>
            {reminderSent ? (
                <Typography variant="body2" color="success.main">
                    Reminder email sent successfully!
                </Typography>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={handleSendReminder}
                    disabled={sendingReminder}
                    sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    data-testid="pre-assessment-cta-send-reminder"
                >
                    {sendingReminder ? 'Sending...' : 'Send Reminder Email'}
                </Button>
            )}
            {reminderError && (
                <Typography variant="body2" color="error">
                    {reminderError}
                </Typography>
            )}
        </Box>
    }

    if (assignment.my_roles.includes('athlete')) return <Box data-custom-id={String(assignment.paymentassignment_id ?? assignment.id)} data-testid="pre-assessment-cta--not-started">
        <Button
            sx={{ mt: 1 }}
            variant="contained"
            color="secondary"
            size="large"
            component={Link}
            to={`/assessments/${assignment.assessment_id}`}
            data-testid="pre-assessment-cta-start"
        >
            Take Assessment
        </Button>
    </Box>

    return <Box display="flex" flexDirection="column" gap={1} data-custom-id={String(assignment.paymentassignment_id ?? assignment.id)} data-testid="pre-assessment-cta--not-started">
        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
            <AddAlert color="error" sx={{ fontSize: 15 }} />
            Assessment not yet started
        </Typography>
        {reminderSent ? (
            <Typography variant="body2" color="success.main">
                Reminder email sent successfully!
            </Typography>
        ) : (
            <Button
                variant="outlined"
                size="small"
                startIcon={<EmailIcon />}
                onClick={handleSendReminder}
                disabled={sendingReminder}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                data-testid="pre-assessment-cta-send-reminder"
            >
                {sendingReminder ? 'Sending...' : 'Send Reminder Email'}
            </Button>
        )}
        {reminderError && (
            <Typography variant="body2" color="error">
                {reminderError}
            </Typography>
        )}
    </Box>

};

export default PreAssessmentCta;
